<template>
  <!-- overlay ที่เขียนเอง ไม่ใช้ bootstrap JS Modal เพราะไม่ต้องพก instance ไปทั้งหน้า
       และห้ามใช้ alert()/confirm() ของเบราว์เซอร์เด็ดขาด — มันบล็อกทุกอย่างบนหน้า -->
  <div v-if="visible" class="repeat-intent-backdrop" @click.self="onDismiss">
    <div
      class="repeat-intent-dialog bg-white rounded shadow p-4"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <div class="d-flex justify-content-between align-items-start">
        <h5 class="mb-0 pe-3">{{ title }}</h5>
        <button type="button" class="btn-close" aria-label="ปิด" @click="onDismiss"></button>
      </div>

      <p class="mt-3 mb-0">{{ body }}</p>
      <p v-if="note" class="mt-2 mb-0 fw-bold text-success">{{ note }}</p>

      <div class="d-grid gap-2 mt-4">
        <!-- ปุ่มแนบสลิปเป็นปุ่มหลักเสมอ เพราะเป็นทางที่ไม่เสียเงินเพิ่ม -->
        <button v-if="canAttachSlip" class="btn btn-success btn-lg" @click="$emit('attach-slip')">
          {{ attachLabel }}
        </button>
        <!-- "บริจาคเพิ่มอีกครั้ง" เป็นปุ่มรองทุกกรณี เพราะเป็นทางที่เสียเงิน
             ห้ามทำให้เด่นกว่านี้ และห้ามให้การกดพลาด/ปิดโมดัล ตกมาที่ปุ่มนี้ -->
        <button class="btn btn-outline-secondary" @click="$emit('donate-again')">
          บริจาคเพิ่มอีกครั้ง
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onUnmounted } from 'vue';
import { formatPrice } from './scripts/utillities';

const props = defineProps({
  // 'none' | 'settled_no_slip' | 'settled_with_slip' | 'awaiting_review'
  action: { type: String, default: 'none' },
  amount: { type: [Number, String], default: 0 },
  donatedAt: { type: String, default: '' },
});

const emit = defineEmits(['attach-slip', 'donate-again', 'dismiss']);

const visible = computed(() => props.action && props.action !== 'none');

// ปิดโดยไม่เลือกอะไร = ไม่เกิดอะไรขึ้น ค่าเริ่มต้นต้องเป็น "ไม่ทำ" เสมอ
// ห้ามให้การปิดตกไปเป็น "บริจาคเพิ่มอีกครั้ง" เพราะจะทำให้การกดพลาดกลายเป็นรายการจ่ายเงินใหม่
const onDismiss = () => emit('dismiss');

const onKeydown = (event) => {
  if (event.key === 'Escape') {
    onDismiss();
  }
};

watch(visible, (isVisible) => {
  if (isVisible) {
    window.addEventListener('keydown', onKeydown);
  } else {
    window.removeEventListener('keydown', onKeydown);
  }
});

onUnmounted(() => window.removeEventListener('keydown', onKeydown));

const canAttachSlip = computed(
  () => props.action === 'settled_no_slip' || props.action === 'awaiting_review',
);

const attachLabel = computed(() =>
  props.action === 'awaiting_review' ? 'แนบสลิปใหม่ทับ' : 'แนบสลิปของรายการเดิม',
);

const THAI_MONTHS = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

// server ส่งมาเป็น 'YYYY-MM-DD HH24:MI' ตามเวลาไทยอยู่แล้ว จึงแยกสตริงตรง ๆ
// ห้ามโยนเข้า new Date() เพราะเบราว์เซอร์จะตีความ timezone แล้ววันที่เพี้ยนได้
const formatThaiDateTime = (value) => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value);
  if (!match) return value;
  const [, year, month, day, hour, minute] = match;
  const monthName = THAI_MONTHS[Number(month) - 1];
  if (!monthName) return value;
  return `${Number(day)} ${monthName} ${Number(year) + 543} เวลา ${hour}:${minute} น.`;
};

const donatedAtText = computed(() => formatThaiDateTime(props.donatedAt));

const amountText = computed(() => {
  const numeric = Number(props.amount);
  return Number.isFinite(numeric) ? formatPrice(numeric) : props.amount;
});

const title = computed(() => {
  switch (props.action) {
    case 'settled_no_slip':
      return 'พบรายการบริจาคที่สำเร็จแล้ว';
    case 'settled_with_slip':
      return 'รายการนี้สำเร็จและแนบสลิปครบแล้ว';
    case 'awaiting_review':
      return 'สลิปของท่านอยู่ระหว่างการตรวจสอบ';
    default:
      return '';
  }
});

const body = computed(() => {
  const when = donatedAtText.value ? ` เมื่อ ${donatedAtText.value}` : '';
  switch (props.action) {
    case 'settled_no_slip':
      return `ระบบพบว่าท่านบริจาค ${amountText.value} บาท ให้โครงการนี้สำเร็จแล้ว${when} แต่ยังไม่ได้แนบสลิป`;
    case 'settled_with_slip':
      return `ระบบพบว่าท่านบริจาค ${amountText.value} บาท ให้โครงการนี้สำเร็จแล้ว${when} และแนบสลิปเรียบร้อยแล้ว`;
    case 'awaiting_review':
      return `ท่านแนบสลิปสำหรับยอด ${amountText.value} บาท ของโครงการนี้ไว้แล้ว${when} เจ้าหน้าที่กำลังตรวจสอบ`;
    default:
      return '';
  }
});

// ทุกกรณีที่เงินเข้าไปแล้วหรือมีสลิปรออยู่ ประโยคที่สำคัญที่สุดคือ "ไม่ต้องโอนซ้ำ"
// จงใจไม่สัญญาว่าตรวจเสร็จภายในกี่วัน — ทั้งระบบยังไม่มีใครรับผิดชอบคิวตรวจ (ดู ADR 0002)
const note = computed(() => (visible.value ? 'ท่านไม่ต้องโอนเงินซ้ำอีก' : ''));
</script>

<style scoped>
.repeat-intent-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1055;
}
.repeat-intent-dialog {
  width: 100%;
  max-width: 32rem;
}
</style>
